import { Space } from "antd";
import { EntityLabel } from "components/Shared/Labels/EntityLabel";
import { RosterAndStaffButton } from "components/Shared/RosterAndStaffButton";
import { useSharedColumnBuilder } from "hooks/useSharedColumnBuilder";
import { groupBy, uniqBy } from "lodash-es";
import moment from "moment";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { DATE_FORMAT } from "~app/constants";
import { ColorIndicator } from "../../../../components/Shared/ColorIndicator.tsx";

export const useFixturesColumns = ({ fixtures }) => {
  const { formatMessage } = useIntl();
  const {
    createFixtureRoundColumn,
    createFixtureStartDateColumn,
    createFixtureStartTimeColumn,
    createFixtureStatusColumn,
    createFixtureResultColumn,
    createFixtureVenueColumn,
    createFixtureStageColumn,
    createFixturePoolColumn,
    createFixtureSeriesColumn,
    createTaskStatusColumn,
  } = useSharedColumnBuilder();

  const setupObjectColumnFilter = (array: Array<any>, objectName: string, textKey: string = "nameLocal") => ({
    onFilter: (value, record) => record?.[objectName]?.id === value,
    filterSearch: true,
    filters: Object.keys(groupBy(array, (x) => x?.[objectName]?.id || null))
      .filter((id) => id && id !== "null")
      .map((id) => ({
        text: array.find((item: any) => item?.[objectName]?.id === id)?.[objectName]?.[textKey],
        value: id,
      })),
  });

  return [
    {
      ...createFixtureRoundColumn(),
      filters: Object.keys(groupBy(fixtures, "round"))
        .filter((round) => round !== "null")
        .map((round) => ({
          text: round,
          value: round,
        })),
      onFilter: (record, value) => value?.round === record,
    },
    {
      title: formatMessage({ id: "columns.fixture.homeEntity" }),
      dataIndex: "homeEntity",
      align: "right",
      render: (value: any) => (
        <Space size="small">
          <EntityLabel title={value?.nameLocal} imageUrl={value?.imageLogoUrl} />
          <ColorIndicator color={value?.uniformTopItemColors?.primary} />
        </Space>
      ),
      width: 200,
    },
    createFixtureResultColumn(),
    {
      title: formatMessage({ id: "columns.fixture.awayEntity" }),
      dataIndex: "awayEntity",
      render: (value: any) => (
        <Space size="small">
          <ColorIndicator color={value?.uniformTopItemColors?.primary} />
          <EntityLabel title={value?.nameLocal} imageUrl={value?.imageLogoUrl} />
        </Space>
      ),
      width: 200,
    },
    {
      ...createFixtureVenueColumn(),
      ...setupObjectColumnFilter(fixtures, "venue"),
    },
    {
      ...createFixtureStartDateColumn(),
      filters: uniqBy(
        fixtures.map((x) => ({
          text: x.startDateTimeLocal && moment(x.startDateTimeLocal).format(DATE_FORMAT),
          value: x.startDateTimeLocal && moment(x.startDateTimeLocal).format(DATE_FORMAT),
        })) || [],
        "value"
      ).sort((a: any, b: any) => {
        const aMixed = new Date(a);
        const bMixed = new Date(b);
        return aMixed < bMixed ? -1 : 1;
      }),
      onFilter: (value, record) => value === moment(record.startDateTimeLocal).format(DATE_FORMAT),
    },
    createFixtureStartTimeColumn(),
    createFixtureStatusColumn(),
    {
      ...createFixtureStageColumn(),
      ...setupObjectColumnFilter(fixtures, "stage"),
    },
    {
      ...createFixturePoolColumn(),
      ...setupObjectColumnFilter(fixtures, "pool"),
    },
    {
      ...createFixtureSeriesColumn(),
      ...setupObjectColumnFilter(fixtures, "series"),
    },
    createTaskStatusColumn(),
    {
      width: 170,
      align: "right",
      render: (_, record) => (
        <Link to={`${record?.fixtureId}`}>
          <RosterAndStaffButton disabled={!record?.areRosterAndStaffEditable} />
        </Link>
      ),
    },
  ];
};
